<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import LoadingIndicator from 'shared/components/LoadingIndicator.vue';
import { useAlert } from 'dashboard/composables';
import markdownToHtmlConverter from './MarkdownToHtmlConverter.js';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  assistantId: {
    type: [String, Number],
    required: true,
  },
  currentProcedures: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['close', 'restore']);

const store = useStore();
const versions = ref([]);
const isLoading = ref(false);
const selectedVersion = ref(null);
const previewProcedures = ref([]);

const showModal = computed({
  get: () => props.show,
  set: value => {
    if (!value) {
      emit('close');
    }
  },
});

async function loadVersions() {
  isLoading.value = true;
  try {
    const data = await store.dispatch(
      'assistants/fetchProcedureVersions',
      props.assistantId
    );
    if (data && data.versions && Array.isArray(data.versions)) {
      versions.value = data.versions;
    } else {
      versions.value = [];
    }
  } catch (error) {
    useAlert(error.message || 'Failed to load procedure versions');
  } finally {
    isLoading.value = false;
  }
}

function formatDate(timestamp) {
  if (!timestamp) return 'Unknown date';
  const date = new Date(timestamp);
  return date.toLocaleString();
}

function selectVersion(version) {
  selectedVersion.value = version;
  // Convert the selected version's procedures to the format expected by ProcedureEditor
  if (version && version.procedures_data) {
    previewProcedures.value = version.procedures_data.map(procedure => ({
      ...procedure,
      // Handle backward compatibility: if procedure has 'libraries', map it to 'knowledgebases'
      knowledgebases: procedure.knowledgebases || procedure.libraries || [],
      // Ensure all arrays exist
      tools: procedure.tools || [],
      attributes: procedure.attributes || [],
      steps: procedure.steps.map(step => ({
        ...step,
        instruction: markdownToHtmlConverter.convertToHtml(
          step.instruction,
          store
        ),
      })),
    }));
  } else {
    previewProcedures.value = [];
  }
}

function restoreVersion() {
  if (!selectedVersion.value) return;

  emit('restore', {
    procedures: selectedVersion.value.procedures_data,
  });

  showModal.value = false;
}

function closeModal() {
  showModal.value = false;
}

// Watch for when the modal opens and load versions
watch(
  () => props.show,
  newValue => {
    if (newValue) {
      loadVersions();
    }
  }
);
</script>

<template>
  <Modal v-model:show="showModal" size="large" :on-close="closeModal">
    <div class="p-8">
      <h2
        class="text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-2"
      >
        {{ $t('ASSISTANT_MGMT.PROCEDURES.VERSION_HISTORY.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-10 mb-6">
        {{ $t('ASSISTANT_MGMT.PROCEDURES.VERSION_HISTORY.DESCRIPTION') }}
      </p>

      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <LoadingIndicator
          size="md"
          :message="$t('ASSISTANT_MGMT.PROCEDURES.VERSION_HISTORY.LOADING')"
        />
      </div>

      <div v-else-if="versions.length === 0" class="text-center py-12">
        <p class="text-n-slate-10">
          {{ $t('ASSISTANT_MGMT.PROCEDURES.VERSION_HISTORY.NO_VERSIONS') }}
        </p>
      </div>

      <div v-else class="flex gap-6 h-[500px]">
        <!-- Left: Versions List -->
        <div
          class="w-96 border-r border-n-slate-3 dark:border-n-slate-8 pr-4 overflow-y-auto"
        >
          <div class="space-y-2">
            <div
              v-for="(version, index) in versions"
              :key="version.version || index"
              data-testid="version-item"
              class="border rounded-lg p-4 cursor-pointer transition-all"
              :class="{
                'bg-woot-50 border-woot-75 dark:bg-woot-700 dark:border-woot-500':
                  selectedVersion === version,
                'border-n-slate-5': selectedVersion !== version,
              }"
              @click="selectVersion(version)"
            >
              <div class="flex items-start justify-between gap-2 mb-2">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-2">
                    <span
                      class="font-bold text-slate-900 dark:text-slate-100 w-full"
                    >
                      {{
                        $t(
                          'ASSISTANT_MGMT.PROCEDURES.VERSION_HISTORY.VERSION',
                          { number: versions.length - index }
                        )
                      }}
                    </span>
                    <span
                      v-if="index === 0"
                      class="px-1.5 py-0.5 text-xs rounded bg-woot-600 dark:bg-woot-200 text-white font-bold"
                    >
                      {{
                        $t('ASSISTANT_MGMT.PROCEDURES.VERSION_HISTORY.CURRENT')
                      }}
                    </span>
                  </div>
                  <p class="text-sm text-n-slate-20 !mb-0">
                    {{ formatDate(version.timestamp) }}
                  </p>
                </div>
              </div>
              <div
                v-if="version.procedures_data && version.procedures_data.length"
                class="mt-2"
              >
                <p class="text-sm text-n-slate-20 !mb-0">
                  {{ version.procedures_data.length }}
                  {{
                    version.procedures_data.length === 1
                      ? 'procedure'
                      : 'procedures'
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Preview -->
        <div class="flex-1 overflow-y-auto">
          <div
            v-if="!selectedVersion"
            class="flex items-center justify-center h-full text-n-slate-10"
          >
            <p>
              {{
                $t('ASSISTANT_MGMT.PROCEDURES.VERSION_HISTORY.SELECT_VERSION')
              }}
            </p>
          </div>
          <div v-else class="h-full">
            <!-- Reuse ProcedureEditor component in read-only mode -->
            <div class="procedure-editor-preview">
              <div
                v-for="(procedure, index) in previewProcedures"
                :key="procedure.id || index"
                class="procedure-card mb-6 border border-n-slate-3 dark:border-n-slate-8 rounded-lg p-6 bg-white dark:bg-n-slate-2 shadow-sm"
              >
                <div class="group flex justify-between items-start mb-4">
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <h3
                        class="text-lg font-semibold text-slate-900 dark:text-slate-100"
                      >
                        {{ procedure.name || 'Untitled Procedure' }}
                      </h3>
                      <span
                        v-if="
                          procedure.kind === 'standard' || procedure.template_id
                        "
                        class="i-lucide-lock w-4 h-4 text-n-blue-11 dark:text-n-blue-10"
                        :title="$t('ASSISTANT_MGMT.PROCEDURES.STANDARD.BADGE')"
                      />
                    </div>
                    <!-- Procedure Description (read-only) -->
                    <p
                      v-if="procedure.description"
                      class="text-sm text-n-slate-10 mt-1"
                    >
                      {{ procedure.description }}
                    </p>
                  </div>
                </div>

                <!-- Steps (read-only) -->
                <div class="steps-container">
                  <div
                    v-for="(step, stepIndex) in procedure.steps"
                    :key="stepIndex"
                    class="step-container group mb-3 mt-4 bg-white dark:bg-n-slate-2 rounded-lg transition-colors relative"
                  >
                    <div class="flex justify-between items-center mb-3">
                      <div class="flex items-center gap-3">
                        <h5
                          class="text-sm font-bold text-n-slate-11 dark:text-n-slate-10"
                        >
                          {{
                            $t('ASSISTANT_MGMT.PROCEDURES.STEP_NUMBER', {
                              number: stepIndex + 1,
                            })
                          }}
                        </h5>
                        <span
                          class="inline-block px-2 py-1 text-xs rounded transition-colors"
                          :class="
                            step.type === 'required'
                              ? 'bg-slate-500 dark:bg-slate-800 text-slate-50 dark:text-slate-100 font-bold'
                              : 'bg-slate-50 dark:bg-slate-950 text-n-slate-12 font-bold'
                          "
                        >
                          {{
                            step.type === 'required'
                              ? $t(
                                  'ASSISTANT_MGMT.PROCEDURES.STEP_TYPES.REQUIRED'
                                )
                              : $t(
                                  'ASSISTANT_MGMT.PROCEDURES.STEP_TYPES.OPTIONAL'
                                )
                          }}
                        </span>
                      </div>
                    </div>

                    <!-- Step Content (read-only) -->
                    <div class="step-editor">
                      <div
                        class="editor-container border border-n-slate-6 dark:border-n-slate-7 rounded-lg min-h-[80px] bg-white dark:bg-n-slate-2 relative"
                      >
                        <div
                          class="tiptap prose max-w-none"
                          v-html="step.instruction"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex justify-end gap-3 mt-6 pt-6 border-t border-n-slate-3 dark:border-n-slate-8"
      >
        <NextButton
          ghost
          :label="$t('ASSISTANT_MGMT.PROCEDURES.VERSION_HISTORY.CANCEL')"
          @click="closeModal"
        />
        <NextButton
          data-testid="version-restore-button"
          :label="$t('ASSISTANT_MGMT.PROCEDURES.VERSION_HISTORY.RESTORE')"
          :disabled="!selectedVersion || versions[0] === selectedVersion"
          @click="restoreVersion"
        />
      </div>
    </div>
  </Modal>
</template>

<style>
.tiptap ul {
  margin: 0;
}
</style>
